/** @format */

import { api } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RMAVerdict = "pass" | "fail";
export type RMAAnalysisStatus = "pending_review" | "approved" | "rejected";

export interface RMACriteria {
  id: string;
  media_id: string;
  verdict: RMAVerdict;
  reason: string | null;
  url: string | null;
  created_at: string | null;
}

export interface RMAAnalysisResult {
  id: string | null;
  media_id: string;
  verdict: RMAVerdict;
  confidence: number | null;
  reasoning: string | null;
  url: string | null;
}

export interface RMAAnalysisResponse {
  analysis_id: string;
  sop_id: string;
  status: RMAAnalysisStatus;
  created_by: string | null;
  total: number;
  passed: number | null;
  failed: number | null;
  results: RMAAnalysisResult[];
  created_at?: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  /** Present when org member submitted; results hidden until super_admin publishes. */
  message?: string;
}

export interface RMAPreviewTile {
  url: string;
  verdict: RMAVerdict;
}

export interface RMAAnalysisSummary {
  analysis_id: string;
  sop_id: string;
  status: RMAAnalysisStatus;
  created_by: string | null;
  created_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  total: number;
  passed: number;
  failed: number;
  /** Thumbnail URLs for history gallery (first images in batch). */
  preview_urls?: string[];
  /** Same tiles with pass/fail for gallery overlays. */
  preview_items?: RMAPreviewTile[];
}

/** Aggregated across all released analyses for the SOP. */
export interface RMASopStats {
  total: number;
  passed: number;
  failed: number;
}

export interface RMAAnalysesListResponse {
  analyses: RMAAnalysisSummary[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
}

export interface RMAPendingReviewItem extends RMAAnalysisSummary {
  sop_title: string | null;
  organization_name: string | null;
  organization_id: string | null;
}

export interface RMAPendingReviewResponse {
  analyses: RMAPendingReviewItem[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const rmaService = {
  /** Fetch all pass/fail criteria images for a given SOP. */
  getCriteria: async (sopId: string): Promise<RMACriteria[]> => {
    const res = await api.get(`/sop/${sopId}/rma/criteria`);
    return (res.data.criteria ?? []) as RMACriteria[];
  },

  /** Add a pass/fail criteria image to a SOP. */
  addCriteria: async (
    sopId: string,
    mediaId: string,
    verdict: RMAVerdict,
    reason?: string,
  ): Promise<RMACriteria> => {
    const res = await api.post(`/sop/${sopId}/rma/criteria`, {
      media_id: mediaId,
      verdict,
      reason: reason ?? null,
    });
    return res.data as RMACriteria;
  },

  /** Delete a criteria entry. */
  deleteCriteria: async (sopId: string, criteriaId: string): Promise<void> => {
    await api.delete(`/sop/${sopId}/rma/criteria/${criteriaId}`);
  },

  /** Update criteria context/reason. */
  updateCriteria: async (
    sopId: string,
    criteriaId: string,
    payload: { reason: string | null },
  ): Promise<RMACriteria> => {
    const res = await api.patch(`/sop/${sopId}/rma/criteria/${criteriaId}`, payload);
    return res.data as RMACriteria;
  },

  /**
   * Run AI analysis on a set of uploaded RMA images.
   * Returns the full analysis response with per-image verdicts.
   */
  analyze: async (
    sopId: string,
    mediaIds: string[],
  ): Promise<RMAAnalysisResponse> => {
    const res = await api.post(`/sop/${sopId}/rma/analyze`, {
      media_ids: mediaIds,
    });
    return res.data as RMAAnalysisResponse;
  },

  /** Get the most recent analysis results for a SOP (may return null analysis). */
  getLatestResults: async (
    sopId: string,
  ): Promise<RMAAnalysisResponse | null> => {
    const res = await api.get(`/sop/${sopId}/rma/results`);
    if (!res.data.analysis_id) return null;
    return res.data as RMAAnalysisResponse;
  },

  /** List all analyses for a SOP with pagination. */
  getAnalyses: async (
    sopId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: RMAAnalysisStatus;
      /** Filter by analysis id substring */
      q?: string;
    },
  ): Promise<RMAAnalysesListResponse> => {
    const res = await api.get(`/sop/${sopId}/rma/analyses`, { params });
    return res.data as RMAAnalysesListResponse;
  },

  /** Totals across all released analyses for this SOP (not a single batch). */
  getSopStats: async (sopId: string): Promise<RMASopStats> => {
    const res = await api.get(`/sop/${sopId}/rma/stats`);
    return res.data as RMASopStats;
  },

  /** Get full detail of a specific analysis including results. */
  getAnalysisDetail: async (
    sopId: string,
    analysisId: string,
  ): Promise<RMAAnalysisResponse> => {
    const res = await api.get(`/sop/${sopId}/rma/analyses/${analysisId}`);
    return res.data as RMAAnalysisResponse;
  },

  /** Publish analysis to the organization after review (super_admin only). */
  publishAnalysis: async (
    sopId: string,
    analysisId: string,
  ): Promise<RMAAnalysisResponse> => {
    const res = await api.put(`/sop/${sopId}/rma/analyses/${analysisId}/publish`);
    return res.data as RMAAnalysisResponse;
  },

  /** Queue of analyses awaiting publication (super_admin only). */
  getPendingReview: async (
    params?: { page?: number; limit?: number },
  ): Promise<RMAPendingReviewResponse> => {
    const res = await api.get("/rma/pending-review", { params });
    return res.data as RMAPendingReviewResponse;
  },

  /** Edit a single analysis result (super_admin only). */
  editResult: async (
    sopId: string,
    analysisId: string,
    resultId: string,
    payload: {
      verdict?: RMAVerdict;
      reasoning?: string | null;
      confidence?: number | null;
    },
  ): Promise<RMAAnalysisResult> => {
    const res = await api.patch(
      `/sop/${sopId}/rma/analyses/${analysisId}/results/${resultId}`,
      payload,
    );
    return res.data as RMAAnalysisResult;
  },
};
